<!--
	Copyright (c) 2021-present, Bäumer, Afonso, Meissner GmbH
	All rights reserved.
-->
<script setup>
import { OnClickOutside } from "@vueuse/components"
import {
    onKeyStroke,
    useCssVar,
    useResizeObserver,
    useScroll,
} from "@vueuse/core"
import { computed, onBeforeUnmount, ref, toRefs, watch } from "vue"

const el = ref(null);
const activeMenuId = ref(null);
const transitionName = ref("forward");
const wasScrollingUp = ref(false);
const navigationVisible = ref(false);
const languageSwitchVisible = ref(false);
const { directions, arrivedState, y } = useScroll(window);

const headerHeight = useCssVar(
    "--header-height",
    ref(document.documentElement)
);

const { stop } = useResizeObserver(el, (entries) => {
    const entry = entries[0];
    const { blockSize: height } = entry.borderBoxSize[0];
    headerHeight.value = `${height}px`;
});

const toggleNavigation = () => {
    navigationVisible.value = !navigationVisible.value;
};

const showNavigation = () => {
    navigationVisible.value = true;
};

const hideNavigation = () => {
    navigationVisible.value = false;
};

const toggleLanguageSwitch = () => {
    languageSwitchVisible.value = !languageSwitchVisible.value;
};

const showLanguageSwitch = () => {
    languageSwitchVisible.value = true;
};

const hideLanguageSwitch = () => {
    languageSwitchVisible.value = false;
};

const navigate = (navItemId) => {
    if (activeMenuId.value) {
        activeMenuId.value = null;
        transitionName.value = "forward";
    } else {
        activeMenuId.value = navItemId;
        transitionName.value = "back";
    }
};

const goBack = () => {
    activeMenuId.value = null;
    transitionName.value = "back";
};

onKeyStroke(["Esc", "Escape"], (e) => {
    e.preventDefault();
    navigationVisible.value = false;
    activeMenuId.value = null;
    transitionName.value = "back";
});

watch(
    [headerHeight],
    ([height]) => {
        headerHeight.value = height;
    },
    { immediate: true }
);

watch(directions, (newDirections) => {
    if (newDirections.top && !newDirections.bottom) {
        wasScrollingUp.value = true;
    } else if (newDirections.bottom) {
        wasScrollingUp.value = false;
    }
});

onBeforeUnmount(() => {
    stop();
    // isScrollLocked.value = false;
});

</script>

<template>
    <header
        :class="[
            { 'the-header--scrolled': y > 10 },
        ]"
        ref="el"
    >
        <slot
            v-bind="{
                navigationVisible,
                showNavigation,
                hideNavigation,
                toggleNavigation,
                goBack,
                navigate,
                activeMenuId,
                toggleLanguageSwitch,
                languageSwitchVisible,
                showLanguageSwitch,
                hideLanguageSwitch,
            }"
        />
    </header>
</template>
